import React, { useEffect, useState } from "react";
import { Modal, Input, Select, Spin, message } from "antd";

import * as api from "./controller";
import HtmlBodyEditor from "./HtmlBodyEditor";
import "./leads.css";

// "3 days ago" without pulling in a dayjs plugin the rest of the app doesn't
// load. Coarse on purpose — the point is "recently" vs "ages ago".
export function relTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  const d = Math.floor(s / 86400);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  const m = Math.floor(d / 30);
  return `${m} month${m === 1 ? "" : "s"} ago`;
}

/**
 * The "book a call" email for a lead — the server renders the booking template
 * for this lead (subject, body, who it would go to), the admin can touch it up,
 * and it is sent exactly as shown.
 *
 * Two things can make sending impossible, and both are said up front with the
 * button disabled rather than discovered on click: no booking template picked
 * (CRM Settings › Leads) and the O365 integration being off. A modal that lets
 * you write a whole email and then refuses to send it is the worst version.
 */
export default function BookingEmailModal({ lead, open, onClose, onSent }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [hasTemplate, setHasTemplate] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [sentCount, setSentCount] = useState(0);
  // A preview that failed to load is not the same as a lead with nothing to
  // send — keep the two apart so the form isn't shown empty as if that were the template.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || !lead) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    api.getLeadBookingEmailPreview(lead.id).then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (r.status >= 200 && r.status < 300 && r.json) {
        const p = r.json;
        setSubject(p.subject || "");
        setBody(p.body_html || "");
        setRecipients(Array.isArray(p.recipients) ? p.recipients : []);
        setHasTemplate(!!p.has_template);
        setEmailEnabled(p.email_enabled !== false);
        setLastSentAt(p.last_sent_at || null);
        setSentCount(p.sent_count || 0);
        // Every address we know for this lead is offered; the preview's
        // recipients are the default, the rest are one click away.
        const seen = {};
        const opts = [];
        const push = (email, label) => {
          const e = (email || "").trim();
          if (!e || seen[e.toLowerCase()]) return;
          seen[e.toLowerCase()] = true;
          opts.push({ value: e, label: label ? `${e} — ${label}` : e });
        };
        (Array.isArray(p.contacts) ? p.contacts : lead.contacts || []).forEach((c) =>
          push(c.email, [c.name, c.title].filter(Boolean).join(" · ")),
        );
        push(lead.email, "practice");
        (p.recipients || []).forEach((e) => push(e));
        setContactOptions(opts);
      } else {
        setFailed(true);
        message.error((r.json && r.json.detail) || "Couldn't load the booking email.");
      }
    });
    return () => {
      cancelled = true;
    };
    // Keyed on the id, not the object: the table behind refetches rows and a
    // new `lead` reference must not reload the preview over the admin's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead && lead.id]);

  const send = async () => {
    if (!recipients.length) {
      message.error("Add at least one recipient.");
      return;
    }
    if (!body.trim()) {
      message.error("The message is empty.");
      return;
    }
    setBusy(true);
    const r = await api.sendLeadBookingEmail(lead.id, { subject, bodyHtml: body, recipients });
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      message.success(`Booking email sent to ${recipients.join(", ")}.`);
      onSent && onSent(r.json || { status: "sent" });
      onClose();
    } else if (r.status === 503) {
      // Email switched off server-side between preview and send.
      message.warning((r.json && r.json.detail) || "Email integration is not enabled.");
    } else if (r.status === 502) {
      message.error((r.json && r.json.detail) || "The mail server refused the message. Nothing was sent.");
    } else {
      message.error((r.json && r.json.detail) || "Couldn't send the booking email.");
    }
  };

  const blocked = loading || failed || !hasTemplate || !emailEnabled;
  const last = relTime(lastSentAt);

  return (
    <Modal
      title={lead ? `Send booking email — ${lead.practice_name || lead.email || "lead"}` : "Send booking email"}
      open={open}
      onCancel={onClose}
      onOk={send}
      okText="Send"
      confirmLoading={busy}
      okButtonProps={{ disabled: blocked || !recipients.length }}
      width={640}
      zIndex={10050}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          {!emailEnabled ? (
            <div className="crml-warn">Email integration is not enabled — this can be previewed but not sent.</div>
          ) : null}
          {!hasTemplate ? (
            <div className="crml-warn">
              No booking template is set — pick one in CRM Settings › Leads before sending.
            </div>
          ) : null}
          {failed ? (
            <div className="crml-warn">The preview didn't load, so there is nothing to send yet. Close and try again.</div>
          ) : null}
          {last ? (
            <div className="crml-last-sent">
              Last sent {last}
              {sentCount > 1 ? ` · ${sentCount} times in total` : ""}
            </div>
          ) : null}

          <label className="crml-field-label" style={{ marginTop: 0 }}>Recipients</label>
          <Select
            mode="tags"
            style={{ width: "100%" }}
            value={recipients}
            onChange={setRecipients}
            options={contactOptions}
            placeholder="Add recipients — pick a contact or type an email"
            tokenSeparators={[",", " "]}
            optionFilterProp="label"
            dropdownStyle={{ zIndex: 10060 }}
          />

          <label className="crml-field-label">Subject</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />

          <label className="crml-field-label">Message</label>
          <HtmlBodyEditor value={body} onChange={setBody} rows={10} placeholder="Message (HTML allowed)…" />
        </>
      )}
    </Modal>
  );
}
